/**
 * Contract type mix by role — how each of the largest role titles is staffed, by
 * approved position (M21, slide 24).
 *
 * THE FINDING IS THE MIX, NOT THE SIZE. Lecturer is the largest title on the
 * establishment, and most of its positions are ICA rather than employee positions.
 * A headcount chart cannot show that; a 100% stack per role can, so every bar is
 * drawn as 100% of its OWN total and the total is printed beside it.
 *
 * POSITIONS, NOT PEOPLE. Every count is approved positions, filled and vacant, and a
 * title's total includes positions whose contract type is not recorded. Permanent
 * and fixed-term positions are employee positions; ICA and casual positions are
 * engagements and sit outside every employee figure on this page.
 *
 * WITHHELD IS DRAWN, NEVER WORKED BACK. Identity withholds a contract-type cell to
 * protect small groups (served null, named in `suppressed`), and a title on fewer
 * than five positions whole (its total null too). The published cells are drawn at
 * their share of the role total and the rest of the bar is one hatched segment that
 * names the withheld cells and prints no number: the role total less the published
 * cells is the withheld cells' sum. No share rests on a withheld cell — where ICA or
 * casual is withheld the share reads "at least", from the published cells alone. A
 * title withheld whole has no bar.
 *
 * Where no cell is withheld, positions with no contract type recorded (Lecturer 888
 * against 886 typed) are drawn in flat grey: a gap in the data, not a withheld figure.
 *
 * The seasonal and temporary titles taken out of the ranking are restated beneath
 * (extras.role_contract_mix_removed), and fixed-term tenure rides with the block
 * (extras.ftc_tenure). All three are dated by the `role_contract_mix` block.
 */
import React, { useMemo, type ReactNode } from 'react';
import { MetricTile, TileRow } from './MetricTile';
import { PanelShell, PanelSkeleton, PanelUnavailable, LegendSwatch } from './PanelShell';
import {
  CATEGORY_COLORS,
  Def,
  ROLE_MIX_NO_TITLE,
  ROLE_MIX_NO_TITLE_LABEL,
  basisLabel,
  categoryLabel,
  fmtNum,
  fmtPct,
  panelDateLabel,
  readExtras,
  type DefLookup,
  type RoleMixRow,
  type WorkforceBasis,
  type WorkforcePanels,
} from './apiContract';

interface Props {
  panels: WorkforcePanels | null;
  /** Part of the shared panel signature. The mix counts every contract type whatever
   *  the toggle says, which is exactly what the window line tells the reader. */
  basis: WorkforceBasis;
  loading: boolean;
  def: DefLookup;
}

// ---------------------------------------------------------------------------
// PALETTE — the shared contract-type hexes, so a colour means the same thing
// here as it does in composition, breakdown and the sessional panel.
// ---------------------------------------------------------------------------

const NOT_RECORDED_COLOR = CATEGORY_COLORS.Unclassified;
/** The same hatch the equity panel uses, so "withheld" reads identically. */
const WITHHELD_FILL = 'repeating-linear-gradient(45deg, #C3CDDD 0 3px, #EDF1F7 3px 6px)';
/** ICA and casual: used only to colour the share text, never a mark. */
const SESSIONAL_TEXT = CATEGORY_COLORS.ICA;

/** Employee contract types first, the house order. */
const MIX_KEYS = ['Permanent', 'FTC', 'ICA', 'Casual'] as const;
type MixKey = (typeof MIX_KEYS)[number];
const SESSIONAL_KEYS: MixKey[] = ['ICA', 'Casual'];

const WITHHELD_TIP =
  'Withheld to protect small groups: a contract type on fewer than five positions for this title, '
  + 'or a figure that would let one be worked out from the figures beside it. These positions are '
  + 'still inside the role total. It is not zero.';

// ---------------------------------------------------------------------------
// DEFINITIONS — the shared contract defines Permanent / FTC / ICA / Casual, so
// those keys are left to it. Server definitions always win over the local wording.
// ---------------------------------------------------------------------------

const defBody = (label: string, body: string, caveats: string[] = []): ReactNode => (
  <div className="space-y-1.5">
    <p className="font-semibold text-white">{label}</p>
    <p>{body}</p>
    {caveats.length ? (
      <ul className="list-disc space-y-0.5 pl-3.5 text-[11px] text-amber-200/90">
        {caveats.map((c, i) => (
          <li key={i}>{c}</li>
        ))}
      </ul>
    ) : null}
  </div>
);

const FALLBACK_DEFS: Record<string, ReactNode> = {
  role_contract_mix: defBody(
    'Contract type mix by role',
    'The largest role titles by approved positions, filled and vacant, each drawn as 100% of its own '
      + 'total and split by contract type.',
    [
      'Positions, not people: a vacant position counts under its title.',
      'Permanent and fixed-term positions are employee positions; ICA and casual positions are not.',
      'Invigilation, marking, moderation and temporary support are shown separately beneath.',
    ],
  ),
  role_mix_total: defBody(
    'Positions in the role',
    'Approved positions carrying that title on the date shown, filled and vacant, every contract type '
      + 'included, and positions with no contract type recorded.',
  ),
  role_mix_sessional: defBody(
    'ICA and casual share',
    'ICA and casual positions as a percentage of all positions carrying that title.',
    [
      'Where ICA or casual is withheld the share is stated as "at least", from the published figures '
        + 'alone. It is never worked out from a withheld figure.',
    ],
  ),
  role_mix_contractor_led: defBody(
    'Contractor-led role',
    'A role where more than half the approved positions carrying the title are ICA or casual — '
      + 'delivered mainly through engagements that sit outside every employee headcount.',
  ),
  role_mix_not_recorded: defBody(
    'Contract type not recorded',
    'Positions counted in the role total with no contract type recorded.',
    [
      'Not a withheld figure: nothing is being protected here, the contract type is simply missing.',
      'They are inside the role total, so the bar is still 100% of the role.',
    ],
  ),
  role_mix_no_title: defBody(
    ROLE_MIX_NO_TITLE_LABEL,
    'Approved positions whose title is not yet recorded. A data-quality residue, not a role, ranked by '
      + 'its size as the board pack ranks it.',
  ),
  role_mix_removed: defBody(
    'Shown separately',
    'Invigilation, marking, moderation and temporary support titles, taken out of the ranking and '
      + 'restated here so every position still adds back.',
  ),
  ftc_tenure: defBody(
    'Fixed-term tenure',
    'Fixed-term employees in post, and how many have two and three years or more of service, measured '
      + 'from their first assignment in the organisation.',
    ['People, not positions.'],
  ),
  suppression: defBody('Withheld', WITHHELD_TIP),
};

/** Server definition first, local wording only where the server has none. */
function extendDefs(def: DefLookup, extra: Record<string, ReactNode>): DefLookup {
  return (key: string) => def(key) ?? extra[key];
}

// ---------------------------------------------------------------------------
// ROW MODEL
// ---------------------------------------------------------------------------

interface Seg {
  key: string;
  pct: number;
  color?: string;
  hatched?: boolean;
  title: string;
}

interface Row {
  /** The served title, kept as the React key. */
  key: string;
  /** What the reader sees: the board's label for the untitled bucket. */
  title: string;
  untitled: boolean;
  /** null: a title on fewer than five positions, withheld whole. */
  total: number | null;
  cells: Record<MixKey, number | null>;
  segs: Seg[];
  /** Cells served as null. */
  withheld: MixKey[];
  /** Positions with no contract type recorded — only where no cell is withheld. */
  notRecorded: number;
  /** Published ICA and casual positions. */
  sessionalPublished: number;
  /** Published ICA and casual over the role total: a floor when a cell is withheld. */
  sessionalPct: number | null;
  sessionalExact: boolean;
  contractorLed: boolean;
}

const displayTitle = (title: string) =>
  title.trim() === ROLE_MIX_NO_TITLE ? ROLE_MIX_NO_TITLE_LABEL : title;

function buildRow(raw: RoleMixRow): Row {
  const key = String(raw.title ?? '');
  const title = displayTitle(key);
  const untitled = title === ROLE_MIX_NO_TITLE_LABEL;
  const cells = Object.fromEntries(
    MIX_KEYS.map((k) => [k, typeof raw[k] === 'number' ? raw[k] : null]),
  ) as Record<MixKey, number | null>;
  const total = typeof raw.total === 'number' ? raw.total : null;
  const base = { key, title, untitled, cells };

  if (total === null) {
    // No denominator is published, so no bar and no share: nothing is rescaled onto
    // cells that are withheld with it.
    return {
      ...base,
      total: null,
      segs: [],
      withheld: [...MIX_KEYS],
      notRecorded: 0,
      sessionalPublished: 0,
      sessionalPct: null,
      sessionalExact: false,
      contractorLed: false,
    };
  }

  const withheld = MIX_KEYS.filter((k) => cells[k] === null);
  const published = MIX_KEYS.reduce((s, k) => s + (cells[k] ?? 0), 0);
  const denom = Math.max(total, published);
  const rest = Math.max(0, total - published);

  const segs: Seg[] = MIX_KEYS.filter((k) => (cells[k] ?? 0) > 0).map((k) => ({
    key: k,
    pct: denom > 0 ? ((cells[k] as number) / denom) * 100 : 0,
    color: CATEGORY_COLORS[k],
    title: `${categoryLabel(k)}: ${fmtNum(cells[k])} of ${fmtNum(total)} positions in ${title}`,
  }));

  if (rest > 0 && denom > 0) {
    segs.push(
      withheld.length
        ? {
            key: 'withheld',
            pct: (rest / denom) * 100,
            hatched: true,
            // No number: the role total less the published cells is the withheld cells' sum.
            title:
              `${withheld.map(categoryLabel).join(' and ')} withheld for ${title} to protect small `
              + 'groups. These positions, and any with no contract type recorded, are inside the role '
              + 'total.',
          }
        : {
            key: 'not-recorded',
            pct: (rest / denom) * 100,
            color: NOT_RECORDED_COLOR,
            title:
              `${fmtNum(rest)} ${rest === 1 ? 'position' : 'positions'} in ${title} with no contract `
              + 'type recorded. Nothing is withheld here: the role total counts them and no contract '
              + 'type claims them.',
          },
    );
  }

  const sessionalPublished = SESSIONAL_KEYS.reduce((s, k) => s + (cells[k] ?? 0), 0);
  const sessionalPct = total > 0 ? (sessionalPublished / total) * 100 : null;

  return {
    ...base,
    total,
    segs,
    withheld,
    notRecorded: withheld.length ? 0 : rest,
    sessionalPublished,
    sessionalPct,
    sessionalExact: SESSIONAL_KEYS.every((k) => cells[k] !== null),
    // On the published cells alone, so the claim never rests on a withheld figure.
    contractorLed: sessionalPct !== null && sessionalPct > 50,
  };
}

const pct0 = (n: number) => `${Math.round(n)}%`;

/** The share beside each bar: exact, a floor from the published cells, or withheld. */
const shareText = (row: Row): string | null => {
  if (row.sessionalPct === null) return null;
  if (row.sessionalExact) return `${pct0(row.sessionalPct)} ICA and casual`;
  return row.sessionalPublished > 0
    ? `at least ${pct0(row.sessionalPct)} ICA and casual`
    : 'ICA and casual withheld';
};

// ---------------------------------------------------------------------------
// STACK ROW — the house BarRow grid, with segments instead of one bar.
// ---------------------------------------------------------------------------

const StackRow: React.FC<{
  label: ReactNode;
  labelTitle: string;
  segs: Seg[];
  /** In place of the bar, for a title withheld whole. */
  note?: ReactNode;
  right: ReactNode;
  rightSub?: ReactNode;
}> = ({ label, labelTitle, segs, note, right, rightSub }) => (
  <div
    className="grid items-center gap-2.5 py-[3px] text-[12.5px]"
    style={{ gridTemplateColumns: 'minmax(0, 208px) 1fr 128px' }}
  >
    <div className="truncate text-gray-700" title={labelTitle}>
      {label}
    </div>
    {note ? (
      <div className="text-[11px] leading-snug text-gray-500">{note}</div>
    ) : (
      <div className="flex h-[15px] overflow-hidden rounded-sm bg-gray-100">
        {segs
          .filter((s) => s.pct > 0)
          .map((s) => (
            <span
              key={s.key}
              title={s.title}
              className="shrink-0"
              style={{
                width: `${s.pct}%`,
                minWidth: 2,
                background: s.hatched ? WITHHELD_FILL : s.color,
              }}
            />
          ))}
      </div>
    )}
    <div className="text-right leading-tight">
      <div className="font-semibold tabular-nums text-heritage-blue">{right}</div>
      {rightSub ? <div className="text-[10px] font-normal tabular-nums">{rightSub}</div> : null}
    </div>
  </div>
);

const MixRowView: React.FC<{ row: Row }> = ({ row }) => (
  <StackRow
    labelTitle={row.title}
    label={
      <span className="flex items-baseline gap-1.5">
        <span className="truncate">{row.title}</span>
        {row.contractorLed ? (
          <span
            className="shrink-0 text-[9px] font-semibold uppercase tracking-wide"
            style={{ color: SESSIONAL_TEXT }}
            title="More than half the positions in this role are ICA or casual — not employee positions."
          >
            contractor-led
          </span>
        ) : null}
      </span>
    }
    segs={row.segs}
    note={
      row.total === null ? (
        <span title={WITHHELD_TIP}>Fewer than five positions — withheld to protect small groups.</span>
      ) : undefined
    }
    right={
      row.total === null ? (
        <span className="text-gray-400">—</span>
      ) : (
        <span title={`${fmtNum(row.total)} approved positions carry the title ${row.title}`}>
          {fmtNum(row.total)}
        </span>
      )
    }
    rightSub={
      shareText(row) ? (
        <span
          style={{ color: row.contractorLed ? SESSIONAL_TEXT : undefined }}
          className={row.contractorLed ? 'font-semibold' : 'text-gray-400'}
          title={
            row.sessionalExact
              ? 'ICA and casual positions as a share of every position carrying this title.'
              : 'ICA or casual is withheld for this title, so the share is stated from the published '
                + 'figures alone.'
          }
        >
          {shareText(row)}
        </span>
      ) : null
    }
  />
);

// ---------------------------------------------------------------------------
// PANEL
// ---------------------------------------------------------------------------

export const RoleContractMixPanel: React.FC<Props> = ({ panels, basis, loading, def }) => {
  const d = useMemo(() => extendDefs(def, FALLBACK_DEFS), [def]);
  const raws = readExtras(panels, 'role_contract_mix');
  const removed = readExtras(panels, 'role_contract_mix_removed');
  const tenure = readExtras(panels, 'ftc_tenure');

  // Served in the board pack's rank order; kept.
  const rows = useMemo(() => (Array.isArray(raws) ? raws : []).map(buildRow), [raws]);
  const removedRows = useMemo(
    () => (Array.isArray(removed?.roles) ? removed.roles : []).map(buildRow),
    [removed],
  );

  // The block's own date and kind, never panels_as_at.
  const dateLabel = panelDateLabel(panels, 'role_contract_mix');

  const totals = useMemo(() => {
    let positions = 0;
    let sessional = 0;
    let withheldWhole = 0;
    let withheldTitles = 0;
    let notRecorded = 0;
    let notRecordedTitles = 0;
    let sessionalExact = true;
    rows.forEach((r) => {
      if (r.total === null) {
        withheldWhole += 1;
        withheldTitles += 1;
        return;
      }
      // Published figures only: a withheld cell is never added, here or anywhere else.
      positions += r.total;
      sessional += r.sessionalPublished;
      if (!r.sessionalExact) sessionalExact = false;
      if (r.withheld.length) withheldTitles += 1;
      if (r.notRecorded > 0) {
        notRecorded += r.notRecorded;
        notRecordedTitles += 1;
      }
    });
    return {
      positions,
      withheldWhole,
      withheldTitles,
      notRecorded,
      notRecordedTitles,
      sessionalPct: positions > 0 ? (sessional / positions) * 100 : null,
      sessionalExact,
      sessionalAny: sessional > 0,
      contractorLed: rows.filter((r) => r.contractorLed).length,
    };
  }, [rows]);

  /** The headline example: the largest contractor-led title, derived rather than named. */
  const lead = useMemo(() => {
    const led = rows.filter((r) => r.contractorLed && r.total !== null);
    if (!led.length) return null;
    const row = led.reduce((m, r) => ((r.total ?? 0) > (m.total ?? 0) ? r : m));
    const ica = row.cells.ICA;
    const casual = row.cells.Casual;
    const dominant =
      ica !== null && (casual === null || ica >= casual)
        ? { cat: 'ICA', count: ica }
        : casual !== null
          ? { cat: 'casual', count: casual }
          : null;
    return { row, dominant };
  }, [rows]);

  const untitledRow = rows.find((r) => r.untitled) ?? null;
  const anyWithheld = [...rows, ...removedRows].some((r) => r.total === null || r.withheld.length);

  return (
    <PanelShell
      title="Contract type mix by role"
      defKey="role_contract_mix"
      def={d}
      window={
        <>
          {dateLabel} — the {rows.length || ''} largest role titles by approved positions, filled and
          vacant, with fixed-term tenure beside them. Every contract type is counted, so it does{' '}
          <b>not</b> follow the basis toggle: ICA and casual positions are drawn here whatever is
          selected above (currently {basisLabel(basis).toLowerCase()}).
        </>
      }
      right={
        rows.length ? (
          <div className="text-right">
            <div className="text-lg font-bold leading-none tabular-nums text-heritage-blue">
              {totals.contractorLed}
            </div>
            <div className="text-[10px] text-gray-500">contractor-led roles</div>
          </div>
        ) : null
      }
      footnote={
        <>
          Employee positions are permanent and fixed-term only; ICA and casual positions are
          engagements, not employment, and sit outside every employee figure on this page.{' '}
          {totals.withheldTitles
            ? `Figures for ${totals.withheldTitles} of these ${rows.length} titles are withheld to protect small
               groups: the published contract types are drawn at their share of the role total and the
               rest of the bar is hatched, with no number printed. A share that would rest on a withheld
               figure reads "at least".`
            : 'No figure for these titles is withheld on this date.'}{' '}
          {untitledRow ? (
            <>
              <i>{ROLE_MIX_NO_TITLE_LABEL}</i> is not a role: it is{' '}
              {untitledRow.total === null
                ? 'fewer than five approved positions'
                : `${fmtNum(untitledRow.total)} approved positions`}{' '}
              whose title is not yet recorded — a data-quality residue, kept visible rather than merged
              away.{' '}
            </>
          ) : null}
          These are the largest titles only, so they do not add up to the establishment.
        </>
      }
    >
      {loading && !panels ? (
        <PanelSkeleton height={420} />
      ) : !panels ? (
        <PanelUnavailable what="The contract type mix by role" />
      ) : !rows.length ? (
        <div className="rounded border border-gray-100 bg-gray-50 p-2.5 text-[11px] leading-snug text-gray-500">
          The contract type mix by role is not available for this date.
        </div>
      ) : (
        <div className="space-y-4">
          <TileRow min="170px">
            <MetricTile
              label="Roles shown"
              value={fmtNum(rows.length)}
              defKey="role_contract_mix"
              def={d}
              size="sm"
              sub="largest titles by approved positions"
            />
            <MetricTile
              label="Positions in these roles"
              value={fmtNum(totals.positions)}
              defKey="role_mix_total"
              def={d}
              size="sm"
              sub={
                totals.withheldWhole
                  ? `${totals.withheldWhole} ${
                      totals.withheldWhole === 1 ? 'title' : 'titles'
                    } on fewer than five positions not added`
                  : 'all contract types, not the whole establishment'
              }
            />
            <MetricTile
              label="ICA and casual share"
              value={
                totals.sessionalPct === null || (!totals.sessionalExact && !totals.sessionalAny) ? (
                  <span className="text-gray-400" title={WITHHELD_TIP}>
                    —
                  </span>
                ) : (
                  `${totals.sessionalExact ? '' : 'at least '}${pct0(totals.sessionalPct)}`
                )
              }
              defKey="role_mix_sessional"
              def={d}
              size="sm"
              sub={
                totals.sessionalExact
                  ? 'of the positions in these roles'
                  : 'from published figures; withheld figures are never added'
              }
            />
            <MetricTile
              label="Contractor-led roles"
              value={fmtNum(totals.contractorLed)}
              defKey="role_mix_contractor_led"
              def={d}
              size="sm"
              sub="over half the positions are ICA or casual"
            />
          </TileRow>

          {/* The slide's actual finding, stated in words before the bars. */}
          {lead ? (
            <div className="rounded border border-teal-200 bg-teal-50 p-2.5 text-[11px] leading-snug text-teal-900">
              <b>{lead.row.title}</b> is the largest contractor-led title at {fmtNum(lead.row.total)}{' '}
              positions
              {lead.dominant ? (
                <>
                  , {fmtNum(lead.dominant.count)} of them {lead.dominant.cat}
                </>
              ) : null}
              {lead.row.sessionalPct !== null ? (
                <>
                  {' '}
                  — {lead.row.sessionalExact ? '' : 'at least '}
                  {pct0(lead.row.sessionalPct)} of the role
                </>
              ) : null}
              . ICA and casual positions are <b>not employee positions</b>: the role is delivered
              mainly through engagements that appear in no employee headcount on this page.
            </div>
          ) : null}

          <div>
            <div className="mb-1.5 flex flex-wrap items-center justify-between gap-x-4 gap-y-1">
              <div className="flex items-center text-[10px] font-semibold uppercase tracking-wide text-gray-500">
                Each role as 100% of its own positions
                <Def def={d} k="role_contract_mix" className="ml-1" />
              </div>
              <div className="flex flex-wrap items-center gap-x-3 gap-y-1">
                {MIX_KEYS.map((k) => (
                  <LegendSwatch
                    key={k}
                    color={CATEGORY_COLORS[k]}
                    label={categoryLabel(k)}
                    defKey={k}
                    def={d}
                  />
                ))}
                {totals.notRecorded > 0 ? (
                  <LegendSwatch
                    color={NOT_RECORDED_COLOR}
                    label="Contract type not recorded"
                    defKey="role_mix_not_recorded"
                    def={d}
                  />
                ) : null}
                {anyWithheld ? (
                  <LegendSwatch color={WITHHELD_FILL} label="Withheld" defKey="suppression" def={d} />
                ) : null}
              </div>
            </div>

            {rows.map((row) => (
              <MixRowView key={row.key} row={row} />
            ))}

            {/* The withheld statement on the face of the panel: a hatched segment must be readable. */}
            <div className="mt-1.5 text-[11px] leading-snug text-gray-500">
              {totals.withheldTitles ? (
                <>
                  Figures for {totals.withheldTitles} of these titles{' '}
                  <span className="inline-flex items-center gap-0.5">
                    are withheld to protect small groups
                    <Def def={d} k="suppression" />
                  </span>{' '}
                  and drawn as the hatched part of the bar, with no number printed — each role total
                  still includes them.
                </>
              ) : (
                <>No figure for these {rows.length} titles is withheld on this date.</>
              )}
              {totals.notRecorded ? (
                <>
                  {' '}
                  A further {fmtNum(totals.notRecorded)}{' '}
                  {totals.notRecorded === 1 ? 'position' : 'positions'} across{' '}
                  {totals.notRecordedTitles} {totals.notRecordedTitles === 1 ? 'title has' : 'titles have'}{' '}
                  no contract type recorded and {totals.notRecorded === 1 ? 'is' : 'are'} drawn in flat
                  grey — a gap in the data, not a withheld figure.
                </>
              ) : null}
            </div>
          </div>

          {/* ── the titles taken out of the ranking, restated so every position adds back ── */}
          <div className="border-t border-gray-100 pt-3">
            <div className="mb-1.5 flex items-center text-[10px] font-semibold uppercase tracking-wide text-gray-500">
              Shown separately: invigilation, marking, moderation and temporary support
              <Def def={d} k="role_mix_removed" className="ml-1" />
            </div>
            {removed ? (
              <>
                {removedRows.map((row) => (
                  <MixRowView key={row.key} row={row} />
                ))}
                <div className="mt-1.5 text-[11px] leading-snug text-gray-500">
                  {typeof removed.positions === 'number'
                    ? `${fmtNum(removed.positions)} positions, taken out of the ranking and restated here so every position still adds back.`
                    : 'Taken out of the ranking and restated here so every position still adds back. Their total is withheld while a title among them has fewer than five positions.'}
                </div>
              </>
            ) : (
              <div className="text-[11px] leading-snug text-gray-500">
                The titles shown separately are not available for this date.
              </div>
            )}
          </div>

          {/* ── fixed-term tenure: people, not positions ── */}
          <div className="border-t border-gray-100 pt-3">
            <div className="mb-1.5 flex items-center text-[10px] font-semibold uppercase tracking-wide text-gray-500">
              Fixed-term employees — people, not positions
            </div>
            {tenure ? (
              <TileRow min="200px">
                <MetricTile
                  label="Fixed-term employees in post"
                  value={fmtNum(tenure.ftc_employees)}
                  defKey="ftc_tenure"
                  def={d}
                  size="sm"
                  sub="people whose most permanent position is fixed-term"
                />
                <MetricTile
                  label="With two years' service or more"
                  value={
                    tenure.two_years_plus === null ? (
                      <span className="text-gray-400" title={WITHHELD_TIP}>
                        —
                      </span>
                    ) : (
                      fmtNum(tenure.two_years_plus)
                    )
                  }
                  defKey="ftc_tenure"
                  def={d}
                  size="sm"
                  sub={
                    <>
                      {tenure.two_years_plus === null
                        ? 'fewer than five — withheld to protect small groups'
                        : tenure.ftc_employees > 0
                          ? `${fmtPct((tenure.two_years_plus / tenure.ftc_employees) * 100)} of fixed-term employees`
                          : null}
                      <br />
                      three years or more:{' '}
                      {tenure.three_years_plus === null
                        ? 'fewer than five — withheld to protect small groups'
                        : fmtNum(tenure.three_years_plus)}
                    </>
                  }
                />
              </TileRow>
            ) : (
              <div className="text-[11px] leading-snug text-gray-500">
                Fixed-term tenure is not available for this date.
              </div>
            )}
          </div>
        </div>
      )}
    </PanelShell>
  );
};
